import React, { useEffect, useRef, useState } from 'react';
import { Shield, X } from 'lucide-react';
import logger from '../../utils/logger';

/**
 * Thin one-off banner shown at the top of the dashboard once the user has
 * quietly clarified ≥3 previously-vague investments (i.e. they've started
 * thinking about how their portfolio reads to others). Marks itself as
 * shown on first render so it never appears again.
 *
 * Independent of the Insights Rollup surface — the Rollup may hide itself
 * when the portfolio is clean, but this nudge still surfaces once.
 *
 * Uses the Bearer token from localStorage (not cookies).
 */
const EstateVaultUpgradeNudge = ({ onGoToEstateVault }) => {
  const [visible, setVisible] = useState(false);
  const firedRef = useRef(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (firedRef.current) return; // Guard against React StrictMode double-invoke in dev.
    firedRef.current = true;
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/portfolio/engagement-status`, {
          headers: { 'Authorization': `Bearer ${token}` }, cache: 'no-store',
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!data.should_show_upgrade_hint) return;
        setVisible(true);
        // Mark as shown so this banner never appears twice.
        await fetch(`${API_URL}/api/portfolio/engagement-status/mark-shown`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (err) {
        logger.error('Continuity System upgrade nudge load failed:', err);
      }
    };
    load();
  }, [API_URL]);

  if (!visible) return null;

  return (
    <div
      className="border border-emerald-100 bg-emerald-50/50 rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="estate-vault-upgrade-nudge"
    >
      <div className="shrink-0 mt-0.5 bg-white p-1.5 rounded border border-emerald-100">
        <Shield className="w-4 h-4 text-emerald-700" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900 font-medium">
          You&apos;ve started thinking about how your portfolio reads to others.
        </p>
        <p className="text-xs text-slate-600 mt-0.5">
          When you&apos;re ready, activating the Continuity System gives your trusted people
          a clear map to what you hold — without giving them access today.
        </p>
        {onGoToEstateVault && (
          <button
            type="button"
            onClick={onGoToEstateVault}
            className="mt-2 text-xs font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
            data-testid="estate-vault-upgrade-cta"
          >
            Explore Continuity System →
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => setVisible(false)}
        className="shrink-0 text-slate-400 hover:text-slate-600 p-1"
        aria-label="Dismiss"
        data-testid="estate-vault-upgrade-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EstateVaultUpgradeNudge;
